import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { PlanFormModal } from '@/features/catalog/PlanFormModal'
import {
  BILLING_KINDS,
  DELIVERY_METHODS,
  PLAN_STATES,
  archivePlan,
  fetchPlans,
  fetchPolicies,
  misconfiguredPlans,
  planPolicyBadge,
  type Plan,
  type PlanPolicyBadge,
  type PlanState,
} from '@/features/catalog/catalogApi'
import { Can } from '@/shared/auth/Can'
import { formatBytes } from '@/shared/lib/format'
import {
  applyLocalList,
  byNumber,
  byOptionalNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { formatMoneyValue } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PlanAccessModal } from '@/features/catalog/PlanAccessModal'
import { usePlanBranchMap } from '@/features/catalog/planAccessApi'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { displayName, hasTranslation } from '@/shared/lib/planName'

const STATE_COLOR: Record<PlanState, string> = {
  DRAFT: 'default',
  ACTIVE: 'green',
  ARCHIVED: 'default',
}

/**
 * Siyosat ustunidagi teg — qaror `planPolicyBadge` da, bu yerda faqat ko'rinish.
 *
 * ⚠️ QIZIL teg yangi va u eng muhim o'zgarish: shu kungacha siyosati
 * UNUTILGAN tarif ham ko'k «Faqat kvota» tegi bilan chizilardi, ya'ni
 * operator ekranda «hammasi joyida» ni ko'rardi. Jonli `tarif-15m` aynan
 * shu tarzda sotuvda turdi va abonent u bilan ulana olmadi.
 *
 * `POLICY` — teg yo'q, siyosat NOMI yoziladi.
 */
const PLAN_POLICY_TAG: Record<PlanPolicyBadge, { color: string; label: string; hint: string } | null> = {
  MISCONFIGURED: {
    color: 'red',
    label: 'catalog.plan.misconfigured',
    hint: 'catalog.plan.misconfiguredHint',
  },
  ADDON: {
    color: 'blue',
    label: 'catalog.plan.quotaOnly',
    hint: 'catalog.plan.quotaOnlyHint',
  },
  POLICY: null,
}

/**
 * Paketlar (Plan) — SOTUV birligi.
 *
 * Siyosat (Policy) alohida sahifada va bu ataylab (01-DOMAIN-MODEL.md §1):
 * bitta siyosat bir necha paketda ishlatiladi, ularni bitta jadvalga
 * qo'shish esa "siyosatni tahrirlash" bilan "paketni tahrirlash" ni
 * chalkashtirardi.
 */
export function PlansPage() {
  const { t, i18n } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Plan | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /*
    ⚠️ Sahifa BOSHQARILADI: `render` ga keladigan `index` joriy sahifadagi
    o'rin, ya'ni boshqarilmagan holatda 2-sahifa yana 1 dan boshlanardi.
  */
  const [paging, setPaging] = useState({ current: 1, pageSize: 25 })

  const list = useLocalListState()

  const plans = useQuery({ queryKey: queryKeys.catalog.plans, queryFn: fetchPlans })
  const rows = useMemo(() => plans.data ?? [], [plans.data])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [displayName(row, i18n.language), row.name],
        filters: {
          state: (row, value) => row.state === value,
          visibility: (row, value) => row.isPublic === (value === 'public'),
          billingKind: (row, value) => row.billingKind === value,
          kind: (row, value) => row.deliveryMethod === value,
        },
        filterState: list.state.filters,
        comparators: {
          name: byText((row) => displayName(row, i18n.language)),
          price: byNumber((row) => row.price.amount),
          quota: byOptionalNumber((row) => row.quotaBytes),
          validity: byOptionalNumber((row) => row.validityDays),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort, i18n.language],
  )

  const firstPage = () => setPaging((current) => ({ ...current, current: 1 }))
  const setFilter = (key: string, value: string | null) => {
    list.setFilter(key, value)
    firstPage()
  }
  const filterSelect = (key: string, placeholder: string, options: readonly { value: string; label: string }[]) => (
    <Select<string>
      allowClear
      style={{ minWidth: 160 }}
      placeholder={placeholder}
      value={list.filter(key) ?? undefined}
      onChange={(value) => setFilter(key, value ?? null)}
      options={[...options]}
    />
  )
  const [accessFor, setAccessFor] = useState<Plan | null>(null)
  const branchMap = usePlanBranchMap()
  const { options: branchChoices } = useActiveBranches()
  const branchNames = new Map(branchChoices.map((b) => [b.id, b.name]))
  const policies = useQuery({ queryKey: queryKeys.catalog.policies, queryFn: fetchPolicies })

  // ⚠️ Siyosat nomi ham TARJIMA bilan: aks holda bitta ekranda paket
  // ruscha, uning siyosati o'zbekcha turardi.
  const policyNames = new Map(
    (policies.data ?? []).map((p) => [p.id, displayName(p, i18n.language)]),
  )
  const broken = misconfiguredPlans(plans.data ?? [])

  const archive = useMutation({
    mutationFn: (id: string) => archivePlan(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.plans })
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.planIndex })
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<Plan> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => (
        <Typography.Text type="secondary">
          {(paging.current - 1) * paging.pageSize + index + 1}
        </Typography.Text>
      ),
    },
    {
      key: 'name',
      title: t('catalog.plan.name'),
      width: 220,
      ...sortColumn(list.sort, 'name'),
      /*
        ⚠️ Tarjimasi YO'Q paket ro'yxatdan YO'QOLMAYDI va QIZIL ham
        bo'lmaydi.

        Bu ikkala chekka ham noto'g'ri bo'lardi:
          - yashirish — operator paketni umuman topa olmasdi;
          - qizil     — «buzuq» degan ma'noni berardi, holbuki tarjimasiz
                        paket mukammal ishlaydi va abonentga kanonik nom
                        bilan ko'rinadi.

        Shuning uchun kanonik nom ODATDAGIDEK chiziladi, yonida esa
        SO'NIQ (secondary) izoh turadi. Sahifadagi qizil rang faqat
        `misconfigured` uchun qolgan — u haqiqatan sotuvni to'xtatadi.
      */
      render: (_, row) => {
        const translated = hasTranslation(row, i18n.language)
        return (
          <>
            <Typography.Text strong>{displayName(row, i18n.language)}</Typography.Text>
            {translated ? null : (
              <Tooltip title={t('catalog.plan.untranslatedHint')}>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {t('catalog.plan.untranslated')}
                </Typography.Text>
              </Tooltip>
            )}
          </>
        )
      },
    },
    {
      key: 'policy',
      title: t('catalog.plan.policy'),
      width: 190,
      render: (_, row) => {
        // ⚠️ Uch holat, ikkita emas. Ilgari bu yerda faqat
        // `policyId === null` tekshirilardi va u ikki BUTUNLAY boshqa
        // narsani bitta ko'k tegga qo'shib yuborardi.
        const tag = PLAN_POLICY_TAG[planPolicyBadge(row)]
        if (tag === null) {
          return policyNames.get(row.policyId ?? '') ?? row.policyId ?? t('common.dash')
        }
        return (
          <Tooltip title={t(tag.hint)}>
            <Tag color={tag.color}>{t(tag.label)}</Tag>
          </Tooltip>
        )
      },
    },
    {
      key: 'billingKind',
      title: t('catalog.plan.billingKind'),
      width: 160,
      render: (_, row) => t(`billingKind.${row.billingKind}`),
    },
    {
      key: 'price',
      title: t('catalog.plan.price'),
      width: 140,
      align: 'right',
      ...sortColumn(list.sort, 'price'),
      render: (_, row) => formatMoneyValue(row.price),
    },
    {
      key: 'quota',
      title: t('catalog.plan.quota'),
      width: 120,
      align: 'right',
      ...sortColumn(list.sort, 'quota'),
      render: (_, row) =>
        row.quotaBytes === null ? (
          <Typography.Text type="secondary">{t('billing.unlimited')}</Typography.Text>
        ) : (
          formatBytes(row.quotaBytes)
        ),
    },
    {
      key: 'validity',
      title: t('catalog.plan.validityDays'),
      width: 120,
      align: 'right',
      ...sortColumn(list.sort, 'validity'),
      render: (_, row) => (row.validityDays === null ? '—' : row.validityDays),
    },
    {
      key: 'kind',
      title: t('subscribers.columns.kind'),
      width: 120,
      render: (_, row) => t(`deliveryMethod.${row.deliveryMethod}`),
    },
    {
      key: 'visibility',
      title: t('catalog.plan.visibility'),
      width: 120,
      render: (_, row) => (row.isPublic ? t('catalog.plan.public') : t('catalog.plan.private')),
    },
    {
      key: 'branches',
      title: t('branch.labelPlural'),
      width: 180,
      render: (_, row) => {
        const ids = branchMap.data?.[row.id] ?? []
        return ids.length === 0 ? (
          <Typography.Text type="secondary">{t('planAccess.everywhere')}</Typography.Text>
        ) : (
          <Space size={[4, 4]} wrap>
            {ids.map((id) => (
              <Tag key={id}>{branchNames.get(id) ?? '…'}</Tag>
            ))}
          </Space>
        )
      },
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`planState.${row.state}`)}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'plan.update',
            },
            {
              key: 'access',
              label: t('planAccess.action'),
              onClick: () => setAccessFor(row),
              permission: 'plan.update',
            },
            {
              key: 'archive',
              label: t('catalog.plan.archive'),
              onClick: () => {
                modal.confirm({
                  title: t('catalog.plan.archiveTitle'),
                  // "O'chirish" DEMAYMIZ: backend `DELETE` ni arxivlash deb
                  // bajaradi va sotilgan paket tarixi saqlanadi
                  content: t('catalog.plan.archiveConfirm'),
                  okButtonProps: { danger: true },
                  okText: t('catalog.plan.archive'),
                  cancelText: t('common.cancel'),
                  onOk: () => archive.mutateAsync(row.id),
                })
              },
              permission: 'plan.delete',
              danger: true,
              divider: true,
              hidden: row.state === 'ARCHIVED',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.plans')}
        description={t('catalog.plan.intro')}
        extra={
          <Can permission="plan.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('catalog.plan.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/*
        ⚠️ Bu ogohlantirish jadvaldagi qizil tegdan ALOHIDA kerak: buzuq
        tarif ro'yxatning istalgan sahifasida bo'lishi mumkin va operator
        uni umuman ko'rmasligi mumkin. Sanoq esa «bittami yoki oltitami»
        degan savolga darhol javob beradi.
      */}
      {broken.length > 0 ? (
        <Alert
          type="error"
          showIcon
          message={t('catalog.plan.misconfiguredAlert', { count: broken.length })}
          description={t('catalog.plan.misconfiguredHint')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Card>
        {/*
          ⚠️ Xato — bo'sh ro'yxatdan AJRATILGAN holat. Bu tekshiruvsiz
          so'rov yiqilganda `plans.data` `undefined` qolar va jadval
          xotirjam «paket yo'q» deb yozardi: operator KATALOG BO'SH deb
          o'ylab, boshqa hech narsa qilmasdi.
        */}
        {plans.isError ? (
          <QueryErrorState error={plans.error} onRetry={() => void plans.refetch()} />
        ) : (
          <>
            <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
              <Input.Search
                allowClear
                value={list.searchText}
                onChange={(e) => {
                  list.setSearchText(e.target.value)
                  firstPage()
                }}
                placeholder={t('catalog.plan.searchPlaceholder')}
                style={{ width: 260, maxWidth: '100%' }}
              />
              {filterSelect(
                'state',
                t('subscribers.columns.status'),
                PLAN_STATES.map((value) => ({ value, label: t(`planState.${value}`) })),
              )}
              {filterSelect('visibility', t('catalog.plan.visibility'), [
                { value: 'public', label: t('catalog.plan.public') },
                { value: 'private', label: t('catalog.plan.private') },
              ])}
              {filterSelect(
                'billingKind',
                t('catalog.plan.billingKind'),
                BILLING_KINDS.map((value) => ({ value, label: t(`billingKind.${value}`) })),
              )}
              {filterSelect(
                'kind',
                t('subscribers.columns.kind'),
                DELIVERY_METHODS.map((value) => ({ value, label: t(`deliveryMethod.${value}`) })),
              )}
              {list.narrowed ? (
                <>
                  <Typography.Text type="secondary">
                    {t('listView.shown', { shown: visible.length, total: rows.length })}
                  </Typography.Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      list.reset()
                      firstPage()
                    }}
                  >
                    {t('dataTable.clearFilters')}
                  </Button>
                </>
              ) : null}
            </Flex>
            <Table<Plan>
              columns={columns}
              dataSource={[...visible]}
              rowKey={(row) => row.id}
              loading={plans.isPending}
              size="middle"
              scroll={{ x: 1420 }}
              pagination={{
                current: paging.current,
                pageSize: paging.pageSize,
                showSizeChanger: true,
              }}
              onChange={(p, filters, sorter, extra) => {
                setPaging({ current: p.current ?? 1, pageSize: p.pageSize ?? 25 })
                localSortChange<Plan>(list.patch)(p, filters, sorter, extra)
              }}
              locale={{
                emptyText:
                  rows.length > 0 && list.narrowed ? (
                    <EmptyState
                      variant="no-data"
                      title={t('listView.noMatch')}
                      description={t('listView.noMatchHint', { total: rows.length })}
                      actionLabel={t('dataTable.clearFilters')}
                      onAction={list.reset}
                    />
                  ) : (
                    <EmptyState
                      variant="no-data"
                      title={t('catalog.plan.emptyTitle')}
                      description={t('catalog.plan.emptyHint')}
                    />
                  ),
              }}
            />
          </>
        )}
      </Card>

      <PlanFormModal
        open={formOpen}
        plan={editing}
        onClose={() => setFormOpen(false)}
      />
      <PlanAccessModal plan={accessFor} onClose={() => setAccessFor(null)} />
    </>
  )
}
